/**
 * Intelligent route preloading utility
 * Preloads frequently accessed routes to improve navigation performance
 */

import { useEffect } from 'react';

interface RoutePreloadConfig {
  path: string;
  preloadFn: () => Promise<any>;
  priority: 'high' | 'medium' | 'low';
}

// Most frequently accessed routes based on typical user flows
const routePreloads: RoutePreloadConfig[] = [
  {
    path: '/task-manager',
    preloadFn: () => import('../pages/TaskManager'),
    priority: 'high'
  },
  {
    path: '/task-details',
    preloadFn: () => import('../pages/TaskDetails'),
    priority: 'high'
  },
  {
    path: '/app-database',
    preloadFn: () => import('../pages/AppDatabase'),
    priority: 'high'
  },
  {
    path: '/latest-report',
    preloadFn: () => import('../pages/LatestReportSummary'),
    priority: 'medium'
  },
  {
    path: '/providers-database',
    preloadFn: () => import('../pages/ProvidersDatabase'),
    priority: 'medium'
  },
  {
    path: '/negotiation-manager',
    preloadFn: () => import('../pages/NegotiationManager'),
    priority: 'low'
  },
  {
    path: '/portfolio',
    preloadFn: () => import('../pages/Portfolio'),
    priority: 'low'
  }
];

const preloadedRoutes = new Set<string>();

/**
 * Preload a specific route
 */
export async function preloadRoute(path: string) {
  if (preloadedRoutes.has(path)) {
    console.log(`[RoutePreloader] Route already preloaded: ${path}`);
    return;
  }

  const config = routePreloads.find(r => r.path === path);
  if (!config) {
    console.warn(`[RoutePreloader] No preload config for: ${path}`);
    return;
  }

  try {
    console.log(`[RoutePreloader] Preloading route: ${path}`);
    await config.preloadFn();
    preloadedRoutes.add(path);
    console.log(`[RoutePreloader] Successfully preloaded: ${path}`);
  } catch (error) {
    console.error(`[RoutePreloader] Failed to preload ${path}:`, error);
  }
}

/**
 * Preload routes by priority
 * Called after initial app load
 */
export async function preloadRoutesByPriority(
  priority: 'high' | 'medium' | 'low' = 'high',
  delay: number = 2000
) {
  // Wait for initial app load and user interaction
  await new Promise(resolve => setTimeout(resolve, delay));

  const priorities = priority === 'high' 
    ? ['high'] 
    : priority === 'medium' 
    ? ['high', 'medium'] 
    : ['high', 'medium', 'low'];

  const routesToPreload = routePreloads.filter(r => 
    priorities.includes(r.priority) && !preloadedRoutes.has(r.path)
  );

  console.log(`[RoutePreloader] Starting preload of ${routesToPreload.length} routes`);

  // Preload sequentially to avoid overwhelming the browser
  for (const config of routesToPreload) {
    await preloadRoute(config.path);
    // Small delay between preloads
    await new Promise(resolve => setTimeout(resolve, 100));
  }

  console.log(`[RoutePreloader] Preload complete. Total preloaded: ${preloadedRoutes.size}`);
}

/**
 * Preload routes based on current user role
 */
export function preloadRoutesForRole(role: string) {
  const roleRoutes: Record<string, string[]> = {
    admin: ['/task-manager', '/app-database', '/portfolio'],
    department_manager: ['/task-manager', '/latest-report', '/app-database'],
    procurement_manager: ['/task-manager', '/providers-database', '/negotiation-manager'],
    provider_manager: ['/task-manager', '/task-details'],
    creative_manager: ['/task-manager', '/app-database'],
    ma_manager: ['/negotiation-manager', '/task-manager'],
  };

  const routes = roleRoutes[role] || ['/task-manager'];
  
  // Preload role-specific routes with a small delay
  setTimeout(() => {
    routes.forEach(path => preloadRoute(path));
  }, 1500);
}

/**
 * Hook to enable intelligent preloading in App
 */
export function useRoutePreloader(user: any, role: string) {
  useEffect(() => {
    if (!user || !role) return;

    // Start with high-priority routes
    preloadRoutesByPriority('high', 2000);

    // Preload role-specific routes
    preloadRoutesForRole(role);

    // Preload medium-priority routes when idle
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        preloadRoutesByPriority('medium', 0);
      });
    } else {
      setTimeout(() => {
        preloadRoutesByPriority('medium', 0);
      }, 5000);
    }
  }, [user, role]);
}
